import { JobSafetyAnalysisType } from '__generated__/graphql';
import { filter, last, maxBy, sortBy } from 'lodash';
import type ICostCode from 'types/CostCode';
import type IProject from 'types/Project';
import type { Nullable } from 'types/util/Nullable';
import { useRequestJsaCandidates, type TJsaReviewCandidate } from './useRequestJsaCandidates';

export interface IDetermineJsaToReviewProps {
  costCodeId?: Nullable<ICostCode['id']>;
  projectId?: Nullable<IProject['id']>;
}
export type TUseDetermineJsaToReview = () => (
  props: IDetermineJsaToReviewProps
) => Promise<TJsaReviewCandidate | undefined>;

export const useDetermineJsaToReview: TUseDetermineJsaToReview = () => {
  const requestRelatedJsaToReview = useRequestJsaCandidates();

  return ({ costCodeId, projectId }) => {
    return requestRelatedJsaToReview(costCodeId, projectId).then((jsaResults) => {
      if (!jsaResults?.length) {
        return undefined;
      }

      const costCodeJsaCollection = filter(jsaResults, { type: JobSafetyAnalysisType.CostCode });
      const taskJsaCollection = filter(jsaResults, { type: JobSafetyAnalysisType.Task });

      // prioritize costCode JSA over task JSA
      if (costCodeJsaCollection.length) {
        if (projectId) {
          const projectScopedJsa = findProjectScopedJsa(costCodeJsaCollection);
          if (projectScopedJsa) {
            return projectScopedJsa;
          }
        }

        const unscopedJsa = findUnscopedJsa(costCodeJsaCollection);
        if (unscopedJsa) {
          return unscopedJsa;
        }
      }

      // if no costCode JSA is found, check for task JSA
      if (taskJsaCollection.length) {
        const projectScopedJsa = findProjectScopedJsa(taskJsaCollection);
        if (projectScopedJsa) {
          return projectScopedJsa;
        }
      }

      return undefined;
    });
  };
};

const findProjectScopedJsa = (jsaResults: TJsaReviewCandidate[]): TJsaReviewCandidate | undefined => {
  const projectScopedJsaResults = filter(jsaResults, { scopeToProject: true });

  if (projectScopedJsaResults.length) {
    // only self and ancestors are in the collection, so the nearest project is the one with the max depth
    // it is theoretically possible to have multiple matches so we match using a filter and get the most recent
    const maxDepth = maxBy(projectScopedJsaResults, 'project.depth')?.project?.depth;
    const deepestProjects = projectScopedJsaResults.filter((jsa) => jsa?.project?.depth === maxDepth);

    const sorted = sortBy(deepestProjects, 'createdOn');
    const result = last(sorted); // get the most recently created JSA

    return result;
  }

  return undefined;
};

const findUnscopedJsa = (jsaResults: TJsaReviewCandidate[]): TJsaReviewCandidate | undefined => {
  const unscopedJsaResults = filter(jsaResults, (jsa) => !jsa.scopeToProject);
  const sorted = sortBy(unscopedJsaResults, 'createdOn');
  const result = last(sorted); // get the most recently created JSA

  return result;
};
